/**
 * Sdílený kontrakt systémového monitoru (btop-like). Používá ho SERVER (sběr z /proc a /sys)
 * i KLIENT (store, komponenty) — proto žádné importy z @client/@server.
 *
 * Snapshot je záměrně rozdělený na TOP-LEVEL SKUPINY podle rychlosti změny. `diffTelemetry`
 * (src/server/utils/telemetryDiff.ts) je shallow merge patch, takže změna jednoho pole pošle
 * celou skupinu. Studené skupiny (`host`, `caps`) se tím po prvním snapshotu už nikdy neposílají.
 *
 * Viz docs/plans/SYSTEM-MONITOR-BTOP.md.
 */

/**
 * Bump při JAKÉKOLIV změně tvaru payloadu — peer s jinou verzí se pak pozná a nezobrazí nesmysly.
 *
 * 6: provázání entit (docs/plans/SYSTEM-MONITOR-PROVAZANOST.md) — `SysmonLanDevice.camera`,
 *    `SysmonUsbDevice.netIfaces`/`blockDevs`, `SysmonSnapshot.usbMissing`. Všechno v jednom
 *    bumpu schválně: `peers.ts` protějšek s jinou verzí odmítne, takže každý bump znamená
 *    koordinovaný deploy RPi i VPS a čtyři bumpy po sobě by znamenaly čtyři výpadky.
 *
 * 7: MikroTik RouterOS jako hostitel (docs/plans/ROUTEROS-MONITOR-A-KONZOLE.md) —
 *    `app` je nově nullable (AP nemá Node proces), `caps.kind`, `runner: "routeros"`,
 *    sekce `routeros`, a `link` u `SysmonLanDevice`/`SysmonMqttClient`.
 */
export const SYSMON_SCHEMA_VERSION = 7;

export type SysmonHostId = string;

// ── Studená část ─────────────────────────────────────────────────────────────

export interface SysmonHostInfo {
  id: SysmonHostId;
  label: string;
  /**
   * `"routeros"` NENÍ hodnota `env.RUNNER` — AP není instance Demirelu. Syntetizuje se
   * při skládání snapshotu z REST odpovědí AP.
   */
  runner: "dev" | "vps" | "rpi" | "routeros";
  hostname: string;
  platform: string;
  arch: string;
  kernel: string;
  /** PRETTY_NAME z /etc/os-release. */
  distro: string | null;
  /** /proc/device-tree/model (RPi) nebo "Model" z /proc/cpuinfo. */
  model: string | null;
  cpuModel: string;
  cpuCores: number;
  /** btime z /proc/stat × 1000. */
  bootTimeMs: number;
  pageSizeB: number;
  clockTicksHz: number;
  appVersion: string;
  nodeVersion: string;
}

export interface SysmonCapabilities {
  /**
   * Druh hostitele. Rozhoduje, které boxy vůbec dávají smysl: `"routeros"` nemá procesy,
   * app health, MQTT ani USB, zato má rádia a klienty. Ostatní příznaky níž popisují
   * Linux a na RouterOS jsou všechny `false`.
   */
  kind: "linux" | "routeros";
  proc: boolean;
  cpuFreq: boolean;
  cpuGovernor: boolean;
  hwmon: boolean;
  thermal: boolean;
  diskstats: boolean;
  statfs: boolean;
  netDev: boolean;
  pressure: boolean;
  /** "all" = vidíme všechny procesy, "own" = hidepid, "none" = /proc není. */
  processes: "all" | "own" | "none";
  /** `get_throttled` v sysfs — na Pi 5 (BCM2712) chybí, na Pi 4 bývá. */
  rpiThrottled: boolean;
  /** hwmon `rpi_volt` → `in0_lcrit_alarm`; náhrada podpěťového bitu na Pi 5. */
  rpiUndervolt: boolean;
  rpiFan: boolean;
  /**
   * `vcgencmd` je spustitelný A `/dev/vcio_gencmd` je čitelný i zapisovatelný.
   * Na Pi 5 je to JEDINÝ zdroj throttlingu (sysfs uzel tam neexistuje) a zároveň
   * jediná cesta k PMIC, tedy k příkonu a napětí 5V railu.
   */
  vcgencmd: boolean;
  /** `/sys/bus/usb/devices` — na Windows a v kontejneru bez /sys chybí. */
  usb: boolean;
  /** Lidsky čitelné důvody degradace pro UI. */
  degradedReasons: string[];
}

// ── Horká část ───────────────────────────────────────────────────────────────

export interface SysmonCpuBreakdown {
  user: number;
  nice: number;
  system: number;
  iowait: number;
  irq: number;
  softirq: number;
  steal: number;
}

export interface SysmonCpuCore {
  index: number;
  usagePercent: number | null;
  freqMhz: number | null;
  tempC: number | null;
  governor: string | null;
}

export interface SysmonCpu {
  totalPercent: number | null;
  breakdown: SysmonCpuBreakdown | null;
  cores: SysmonCpuCore[];
  freqMhz: number | null;
  freqMinMhz: number | null;
  freqMaxMhz: number | null;
  tempC: number | null;
  tempCritC: number | null;
  /**
   * ⚠ `null` znamená „stroj tohle nehlásí", ne nulu. RouterOS load average ani počty
   * procesů nemá, a `[0,0,0]` vedle svítícího „CPU 23 %" se čte jako „metriky jsou
   * rozbité" — přesně ta tichá nepravda, které se vyhýbá i `SysmonLinkInfo`.
   */
  loadAvg: [number, number, number] | null;
  procRunning: number | null;
  procTotal: number | null;
  ctxSwitchesPerS: number | null;
  interruptsPerS: number | null;
  forksPerS: number | null;
  uptimeS: number;
  pressureSome10: number | null;
  /** true = první vzorek, rychlosti ještě nedávají smysl. */
  warmingUp: boolean;
}

/** zram je komprimovaná RAM, ne disk — bez rozlišení vypadá 20 % swapu jako vážný problém. */
export type SysmonSwapKind = "zram" | "partition" | "file" | "unknown";

export interface SysmonSwap {
  totalB: number;
  usedB: number;
  freeB: number;
  usedPercent: number;
  kind: SysmonSwapKind;
  /**
   * Jen u zram: kolik RAM data po kompresi opravdu zabírají (`mm_stat`, 2. pole).
   * Na RPi bývá poměr ~4:1, takže 858 MiB „swapu" jsou reálně ~211 MiB paměti.
   */
  compressedB: number | null;
}

export interface SysmonMemory {
  totalB: number;
  /** btop sémantika: total − available. Hlavní číslo v UI. */
  usedB: number;
  /** `free -h` sémantika: total − free − buffers − (cached + sreclaimable − shmem). */
  usedNoCacheB: number;
  availableB: number;
  freeB: number;
  cachedB: number;
  buffersB: number;
  sharedB: number;
  sreclaimableB: number;
  dirtyB: number;
  usedPercent: number;
  /** null = žádný swap. */
  swap: SysmonSwap | null;
  pressureSome10: number | null;
}

export interface SysmonDisk {
  mountpoint: string;
  device: string;
  fstype: string;
  totalB: number;
  usedB: number;
  freeB: number;
  usedPercent: number;
  /**
   * Čitelné jméno svazku u device-mapperu — `/dev/mapper/vgdata-immich` → `vgdata-immich`.
   * `null` u běžných oddílů, kde `device` sám o sobě stačí.
   */
  deviceLabel: string | null;
  /** Např. "nvme0n1" nebo "dm-0" — klíč do /proc/diskstats. */
  ioDevice: string | null;
  readBps: number | null;
  writeBps: number | null;
  busyPercent: number | null;
  tempC: number | null;
}

export interface SysmonNetIface {
  name: string;
  ipv4: string | null;
  ipv6: string | null;
  /** Maskovaná MAC (OUI zůstane): "b8:27:eb:**:**:**". */
  mac: string | null;
  up: boolean;
  speedMbps: number | null;
  mtu: number | null;
  rxBps: number | null;
  txBps: number | null;
  rxTotalB: number;
  txTotalB: number;
  rxErrors: number;
  txErrors: number;
  rxDropped: number;
  txDropped: number;
  /**
   * Kolikrát linka spadla od startu. `null` = stroj to nehlásí.
   * Nenulová hodnota u jinak zdravého rozhraní je jediná stopa po flapování.
   */
  linkDowns: number | null;
}

export interface SysmonSensor {
  key: string;
  label: string;
  tempC: number;
  critC: number | null;
  source: "hwmon" | "thermal";
  chip: string | null;
}

export interface SysmonRpiThrottle {
  undervoltageNow: boolean;
  freqCappedNow: boolean;
  throttledNow: boolean;
  softTempLimitNow: boolean;
  undervoltageEver: boolean;
  freqCappedEver: boolean;
  throttledEver: boolean;
  softTempLimitEver: boolean;
  raw: string;
}

export interface SysmonRpi {
  /**
   * Osm bitů throttlingu. Na Pi 4 ze sysfs, na Pi 5 z `vcgencmd` — tam sysfs uzel
   * NEEXISTUJE. `null` = ani jeden zdroj není k dispozici.
   */
  throttled: SysmonRpiThrottle | null;
  /** Odkud `throttled` přišel; UI podle toho vysvětluje, co dělat, když je null. */
  throttledSource: "sysfs" | "vcgencmd" | null;
  /**
   * Náhrada za chybějící `get_throttled` na Pi 5 bez `vcgencmd`: hwmon `rpi_volt`
   * → `in0_lcrit_alarm`. `true` = firmware právě hlásí podpětí. Nerozlišuje „teď" vs „od zapnutí".
   */
  undervoltageAlarm: boolean | null;
  fanRpm: number | null;
  fanPwm: number | null;
  /** Příkon celé desky ze součtu V×A přes rails PMIC (`vcgencmd pmic_read_adc`). */
  powerW: number | null;
  /** Napětí vstupního 5V railu — přímé měření zdroje, ne binární příznak podpětí. */
  ext5vV: number | null;
  tripPoints: { type: string; tempC: number }[] | null;
}

export type SysmonModuleState = "RUNNING" | "OFFLINE" | "ERROR";

export interface SysmonApp {
  pid: number;
  uptimeS: number;
  rssB: number;
  heapUsedB: number;
  heapTotalB: number;
  externalB: number;
  arrayBuffersB: number;
  /** Vlastní proces vůči celému stroji (0..100). */
  cpuPercent: number | null;
  eventLoopLagP50Ms: number | null;
  eventLoopLagP99Ms: number | null;
  eventLoopLagMaxMs: number | null;
  activeResources: number;
  pm2: { instanceId: number | null; restartsAtBoot: number | null } | null;
  dbPool: { total: number; idle: number; waiting: number; max: number } | null;
  pg: SysmonPgStats | null;
  /**
   * `droppedTotal` = přetečení fronty. `gateDroppedTotal` = zprávy zahozené bránou dřív,
   * než se do fronty vůbec dostaly (některý z modulů db/ws/memory/rules není RUNNING).
   * Nenulová druhá hodnota při nulové první znamená, že aplikace nedokončila start.
   */
  mqtt: { queueLength: number; droppedTotal: number; gateDroppedTotal: number } | null;
  webpushOutboxPending: number | null;
  ws: { clients: number; sysmonSubscribers: number };
  modules: Record<string, SysmonModuleState>;
}

/** Per-second delty z pg_stat_database (přepočet sdílený s pgMon.calculatePerSecond). */
export interface SysmonPgStats {
  databaseName: string;
  transactionsPerS: number;
  diskReadsPerS: number;
  cacheHitsPerS: number;
  /** Už v procentech (0..100). */
  cacheHitRatio: number;
  rowsInsertedPerS: number;
  rowsUpdatedPerS: number;
  rowsDeletedPerS: number;
}

// ── MQTT (broker + klienti + zařízení) ───────────────────────────────────────

/**
 * Agregáty z `$SYS/broker/*`. Mosquitto per-klient statistiky v `$SYS` NEVYSTAVUJE —
 * identita klientů proto přichází z `$SYS/broker/log/N` (viz `SysmonMqttClient`).
 */
export interface SysmonMqttBroker {
  version: string | null;
  uptimeS: number | null;
  clientsConnected: number | null;
  clientsTotal: number | null;
  clientsMaximum: number | null;
  subscriptions: number | null;
  retainedMessages: number | null;
  messagesReceivedPerS: number | null;
  messagesSentPerS: number | null;
  bytesReceivedPerS: number | null;
  bytesSentPerS: number | null;
  publishDropped: number | null;
  heapB: number | null;
  /** Stáří posledních `$SYS` dat v ms — broker je publikuje po `sys_interval` (default 10 s). */
  ageMs: number | null;
}

export interface SysmonMqttClient {
  /** `ip:port` — jediný spolehlivý klíč; client ID se u části spojení dozvíme až po reconnectu. */
  key: string;
  ip: string;
  port: number;
  /** Z `$SYS/broker/log/N`; `null`, dokud se klient znovu nepřipojí. */
  clientId: string | null;
  username: string | null;
  /** 4 = MQTT 3.1.1, 5 = MQTT 5. */
  protocol: number | null;
  cleanSession: boolean | null;
  keepaliveS: number | null;
  connectedAtMs: number | null;
  /** Spojení z tohoto stroje (127.0.0.1 / ::1) — typicky sama aplikace. */
  local: boolean;
  /**
   * MAC z ARP tabulky nebo skenu sítě, **nemaskovaná**.
   *
   * Jinde v monitoru se MAC maskuje na OUI (`maskMac`), tady schválně ne: všechna PicoW
   * sdílejí stejné OUI, takže maskovaná adresa je u všech identická a konkrétní kus
   * neidentifikuje — a přesně o to tu jde. Stránka je admin-only a jde o vlastní LAN.
   */
  mac: string | null;
  /** Výrobce podle OUI (z nmapu nebo z vestavěné tabulky). */
  vendor: string | null;
  /** Jak je klient připojený — wifi na našem AP, nebo za uplinkem. Viz `SysmonLinkInfo`. */
  link: SysmonLinkInfo | null;
}

/**
 * Zařízení viděné na LAN, které NEMÁ spojení na broker. Smysl: PicoW, kterému spadlo MQTT,
 * ale na WiFi pořád je — z pohledu MQTT je neviditelné, a přitom je to ten nejzajímavější
 * stav (jinak by stačilo koukat na seznam klientů).
 */
/**
 * Jak je zařízení připojené do sítě.
 *
 * ⚠ **Rozlišit jde jen to, co je vidět.** `wifi-ap` je jistota: zařízení je v registrační
 * tabulce našeho MikroTiku, i se signálem a pásmem. Všechno ostatní sedí za uplinkem AP
 * a drát od wifi routeru **rozlišit NELZE** — TP-Link Archer AX20 nemá API ani SSH.
 *
 * Proto se to jmenuje `uplink` a ne `ethernet`. Tvrdit „ethernet" u zařízení, které může
 * viset na wifi routeru, by byla lež — a přesně takové tiché nepřesnosti stály tenhle
 * projekt už dost hledání.
 */
export interface SysmonLinkInfo {
  kind: "wifi-ap" | "uplink" | "unknown";
  /** Rádio AP, na kterém klient sedí: `wifi1` (2,4 GHz) / `wifi2` (5 GHz). */
  iface: string | null;
  ssid: string | null;
  /** Např. `2ghz-n`, `5ghz-ax`. */
  band: string | null;
  signalDbm: number | null;
  /**
   * Rychlost asociace v BAJTECH za sekundu, z pohledu AP: `tx` je AP → klient (pro klienta
   * download), `rx` klient → AP. RouterOS to hlásí v bitech, převod dělá `rosBitsToBytes()`.
   */
  txRateBps: number | null;
  rxRateBps: number | null;
  /** Jak dlouho je klient asociovaný. */
  uptimeS: number | null;
}

export interface SysmonLanDevice {
  ip: string;
  mac: string;
  vendor: string | null;
  /** Jak je připojené; `null` = zdroj (AP) není nakonfigurovaný nebo nedostupný. */
  link: SysmonLinkInfo | null;
  /**
   * Jméno kamery, když MAC sedí s některým záznamem v tabulce `Foscam`.
   *
   * Kamery se k brokeru nepřipojují, takže bez tohohle jsou v monitoru jen jako
   * neidentifikovaná IP — přitom jde o čtyři z mála zařízení, která na téhle síti mají jméno.
   */
  camera: string | null;
  /** Odkud údaj pochází — ARP je zadarmo, sken najde i to, co s námi nemluví. */
  source: "arp" | "scan";
  /** Stáří záznamu v ms; u ARP je to stáří tabulky, u skenu doba od posledního sweepu. */
  ageMs: number | null;
}

/** Telemetrie, kterou o sobě hlásí PicoW na `zige/<dev>/diag`. */
export interface SysmonMqttDeviceDiag {
  ip: string | null;
  rssiDbm: number | null;
  uptimeS: number | null;
  memFreeB: number | null;
  /** Důvod posledního resetu — odpovídá na otázku, jestli watchdog vůbec kdy zabral. */
  resetCause: string | null;
  bootCount: number | null;
  firmware: string | null;
  /** Kolik ROM našel poslední sken 1-Wire — diagnostika DS18B20 bez zásahu do chování. */
  onewireRoms: number | null;
  tsMs: number;
}

export interface SysmonMqttDevice {
  /** Prostřední segment topicu: `zige/meteo/temp/val` → `meteo`. */
  name: string;
  /** Počet topiců z retained `zige/<dev>/config`. */
  topicCount: number;
  /** Nejnovější hodnota napříč topicy zařízení (lokální čas přijetí). */
  lastSeenMs: number | null;
  online: boolean;
  /** Obsah `zige/<dev>/status` (Last Will); `null` = zařízení LWT neposílá. */
  status: string | null;
  hasConfig: boolean;
  configMtimeMs: number | null;
  diag: SysmonMqttDeviceDiag | null;
}

export interface SysmonMqtt {
  /** URL brokeru, na který je tenhle hostitel připojený. */
  brokerUrl: string;
  broker: SysmonMqttBroker | null;
  clients: SysmonMqttClient[];
  devices: SysmonMqttDevice[];
  /** Na síti, ale bez MQTT spojení. Prázdné pole = sken neběží nebo nic nenašel. */
  lan: SysmonLanDevice[];
}

// ── USB ──────────────────────────────────────────────────────────────────────

/**
 * Která služba má tenhle port v konfiguraci.
 *
 * Existuje kvůli jediné otázce, na kterou se jinak odpovídá čtením logu: *proč mi Tracer
 * nejede?* Cesty `/dev/ttyUSBn` totiž nejsou stabilní — pořadí přiděluje jádro podle toho,
 * v jakém pořadí se zařízení ohlásí, a v konfiguraci (`transportTarget`) je uložená holá
 * cesta. Po přeenumerování tedy může konfigurace ukazovat na úplně jiný převodník.
 */
export interface SysmonUsbClaim {
  service: "tracer" | "pip" | "bms";
  slug: string;
  /** Cesta zapsaná v konfiguraci zařízení. */
  configuredPath: string;
  /** Stav běžícího driveru; `null` = záznam v databázi je, ale driver neběží. */
  status: string | null;
}

/** Sériový port za USB převodníkem. */
export interface SysmonUsbTty {
  /** "/dev/ttyUSB0" */
  path: string;
  /**
   * Cesta z `/dev/serial/by-id`, která přežije přeenumerování. `null` znamená, že ji
   * zařízení neumožňuje — typicky proto, že nemá sériové číslo (levné CH340 ho nemají).
   * Takový převodník nejde spolehlivě odlišit od druhého kusu téhož typu.
   */
  byId: string | null;
  claim: SysmonUsbClaim | null;
}

export interface SysmonUsbDevice {
  /** Pozice na sběrnici ("1-2.2"). Stabilní klíč pro React i pro řazení stromu. */
  key: string;
  busnum: number | null;
  devnum: number | null;
  /** Čtyřmístné hex identifikátory, jak je hlásí zařízení ("0403", "6001"). */
  vendorId: string | null;
  productId: string | null;
  manufacturer: string | null;
  product: string | null;
  /**
   * Plné sériové číslo. NEmaskuje se — na rozdíl od MAC v `SysmonNetIface` je to tady
   * jediný údaj, kterým se dva stejné převodníky od sebe odliší, a to je celý smysl
   * téhle sekce. Stránka /system je admin-only na čtyřech vrstvách.
   */
  serial: string | null;
  /** Vyjednaná rychlost. Disk na 480 místo 5000 znamená špatný kabel nebo port. */
  speedMbps: number | null;
  /** Odběr hlášený v deskriptoru. Součet přes hub napoví, jestli není přetížený. */
  maxPowerMa: number | null;
  /** Ovladač navázaný na první rozhraní: ftdi_sio, ch341, xr_serial, uas, r8152… */
  driver: string | null;
  /** Třída zařízení z deskriptoru ("09" = hub, "ff" = vendor specific). */
  deviceClass: string | null;
  /** Zanoření ve stromu — kreslí odsazení pod hubem. */
  depth: number;
  /** Sériové porty za tímhle zařízením; prázdné u všeho, co není převodník. */
  ttys: SysmonUsbTty[];
  /**
   * Síťová rozhraní za tímhle zařízením (`eth1`). Odpovídá na „který z těch adaptérů je
   * vlastně eth1" — z produktového jména se to u dvou stejných kusů poznat nedá.
   */
  netIfaces: string[];
  /** Bloková zařízení za USB diskem (`sda`). Spojí řádek stromu s diskem v sekci Disky. */
  blockDevs: string[];
}

// ── RouterOS (MikroTik AP) ───────────────────────────────────────────────────

/** Rádio AP. Obě mají stejné SSID, takže je od sebe odliší jen jméno rozhraní. */
export interface SysmonRouterOsRadio {
  name: string;
  /**
   * Rádio je administrativně vypnuté (`/interface wifi disable`). **Jediný příznak,
   * který opravdu znamená „nevysílá".**
   */
  disabled: boolean;
  /**
   * Je asociovaný aspoň jeden klient?
   *
   * ⚠ Odpovídá to poli `running` z RouterOS, které u wifi rozhraní **NEZNAMENÁ „rádio
   * jede"**. RouterOS 7 hlásí `running=false`, jakmile odejde poslední klient — rádio
   * přitom dál normálně vysílá. Ověřeno na wAP ax: `running=false` současně
   * s `disabled=false`, `bound=true` a prázdnou registrační tabulkou. Zobrazit to jako
   * „rádio vypnuté" by byla lež pokaždé, když je síť prázdná.
   */
  hasPeers: boolean;
  /**
   * Kolikrát `hasPeers` spadlo na `false`. U wifi to tedy NENÍ výpadek rádia, ale
   * „kolikrát se síť vyprázdnila" — nepřímá stopa po klientech, kterým padá spojení.
   */
  linkDowns: number | null;
  ssid: string | null;
  band: string | null;
  channel: string | null;
  txPowerDbm: number | null;
  registeredPeers: number | null;
}

/**
 * Neteplotní údaj ze `/system/health` — napětí, proud, otáčky. Teploty jdou do standardní
 * sekce `sensors`; tohle je zbytek, který by se do `SysmonSensor` (má jen `tempC`) nevešel.
 */
export interface SysmonRouterOsReading {
  name: string;
  value: number;
  /** Jednotka tak, jak ji hlásí RouterOS: `V`, `A`, `RPM`… */
  unit: string;
}

/** Klient asociovaný na wifi AP. Doplněný o IP z ARP — AP samo IP adresy nezná. */
export interface SysmonRouterOsClient {
  mac: string;
  /** Doplněno joinem přes ARP tabulku tohohle stroje; `null` = zatím jsme ho na IP nechytili. */
  ip: string | null;
  iface: string;
  ssid: string | null;
  band: string | null;
  signalDbm: number | null;
  /**
   * Rychlost asociace v BAJTECH za sekundu, z pohledu AP: `tx` je AP → klient (pro klienta
   * download), `rx` klient → AP. RouterOS to hlásí v bitech, převod dělá `rosBitsToBytes()`.
   */
  txRateBps: number | null;
  rxRateBps: number | null;
  uptimeS: number | null;
  /** Jméno kamery, když MAC sedí s tabulkou `Foscam`. */
  camera: string | null;
}

export interface SysmonRouterOsLogRow {
  time: string;
  topics: string;
  message: string;
}

/**
 * To, co má AP navíc oproti Linuxu. Zbytek (CPU, paměť, teploty, rozhraní, flash)
 * se skládá do standardních sekcí, takže se vykreslí existujícími boxy.
 */
export interface SysmonRouterOs {
  identity: string | null;
  model: string | null;
  version: string | null;
  /** RouterBOOT umí zaostávat za RouterOS — je to častá příčina divného chování. */
  firmware: string | null;
  firmwareUpgrade: string | null;
  /** Kde se AP právě našlo. Nikde se nekonfiguruje — jen se ukazuje. */
  address: string | null;
  /**
   * Jak se k němu došlo. `mndp-*` = zařízení se ohlásilo samo, `arp-mac` = záložní cesta
   * přes sken podle MAC.
   */
  discoveredVia: "mndp-identity" | "mndp-mac" | "mndp-single" | "arp-mac" | null;
  /** Napětí a další neteplotní čidla. U PoE modelu je tu vstupní napětí injektoru. */
  readings: SysmonRouterOsReading[];
  /** Vadné bloky flash v procentech. Roste = flash dochází. */
  badBlocksPercent: number | null;
  /** Zapsané sektory za celou životnost — opotřebení flash. */
  writeSectorsTotal: number | null;
  /**
   * Časová zóna AP. ⚠ Řádky v `logs` jsou v LOKÁLNÍM čase AP, ne v čase prohlížeče ani
   * serveru. Bez tohohle údaje by se čas v logu tiše porovnával s něčím jiným.
   */
  timezone: string | null;
  radios: SysmonRouterOsRadio[];
  clients: SysmonRouterOsClient[];
  logs: SysmonRouterOsLogRow[];
}

export interface SysmonSnapshot {
  schemaVersion: number;
  hostId: SysmonHostId;
  ts: number;
  intervalMs: number;
  host: SysmonHostInfo;
  caps: SysmonCapabilities;
  cpu: SysmonCpu;
  mem: SysmonMemory;
  disks: SysmonDisk[];
  net: SysmonNetIface[];
  sensors: SysmonSensor[];
  rpi: SysmonRpi | null;
  /** `null` tam, kde aplikace nemá spojení na broker (VPS, Windows). */
  mqtt: SysmonMqtt | null;
  /** Prázdné pole tam, kde sběrnici nevidíme — sekce dává smysl všude, jen tu nic není. */
  usb: SysmonUsbDevice[];
  /**
   * Nakonfigurované porty, které na stroji NEEXISTUJÍ (vytažený převodník, přeenumerování).
   * Ze stromu hardwaru se takový stav poznat nedá — proto je vedle něj, ne v něm.
   */
  usbMissing: SysmonUsbClaim[];
  /**
   * `null` u hostitele, který není instancí Demirelu — MikroTik AP nemá Node proces,
   * pm2, databázový pool ani event loop. UI podle toho `AppHealthBox` skryje.
   */
  app: SysmonApp | null;
  /** `null` u všeho, co není RouterOS. */
  routeros: SysmonRouterOs | null;
}

// ── Procesy (samostatný kanál) ───────────────────────────────────────────────

export type SysmonProcSortKey = "cpu" | "mem" | "pid" | "name" | "user" | "threads" | "time";

export const SYSMON_PROC_SORT_KEYS: SysmonProcSortKey[] = ["cpu", "mem", "pid", "name", "user", "threads", "time"];

export interface SysmonProcQuery {
  hostId: SysmonHostId;
  sort: SysmonProcSortKey;
  reversed: boolean;
  tree: boolean;
  filter: string;
  includeKernel: boolean;
  limit: number;
  offset: number;
}

export type SysmonProcState = "R" | "S" | "D" | "Z" | "T" | "t" | "X" | "I" | "?";

export interface SysmonProcess {
  pid: number;
  ppid: number;
  name: string;
  /** REDAKOVANÁ příkazová řádka — nikdy syrová (viz services/sysmon/redact.ts). */
  cmd: string;
  user: string;
  uid: number;
  state: SysmonProcState;
  threads: number;
  /** % celého stroje (btop proc_per_core=false). */
  cpuPercent: number;
  memRssB: number;
  memPercent: number;
  memVirtB: number;
  cpuTimeS: number;
  /** Věk v sekundách (z /proc/uptime, NE z btime — ten po NTP skoku poskočí). */
  ageS: number;
  nice: number;
  lastCore: number | null;
  kernel: boolean;
  /** Jen ve stromovém režimu. */
  depth?: number;
}

export interface SysmonProcessPage {
  schemaVersion: number;
  hostId: SysmonHostId;
  ts: number;
  query: SysmonProcQuery;
  /** Po filtru, před stránkováním. */
  total: number;
  totalUnfiltered: number;
  rows: SysmonProcess[];
  truncated: boolean;
  /** Kolik procesů se nepodařilo přečíst (zmizely / EACCES) — jen diagnostika. */
  skipped: number;
}

// ── Přehled hostitelů ────────────────────────────────────────────────────────

export interface SysmonHostSummary {
  id: SysmonHostId;
  label: string;
  local: boolean;
  reachable: boolean;
  error: string | null;
  lastOkTs: number | null;
}

export interface SysmonHostStateEvent {
  hostId: SysmonHostId;
  reachable: boolean;
  error: string | null;
  lastOkTs: number | null;
}

// ── Limity payloadu (vynucené clampSnapshot) ─────────────────────────────────

export const SYSMON_LIMITS = {
  cores: 64,
  ifaces: 32,
  disks: 32,
  sensors: 64,
  tripPoints: 16,
  mqttClients: 64,
  mqttDevices: 32,
  /** Sken vidí celou podsíť; bez stropu by /24 s plným DHCP nafoukla snapshot. */
  lanDevices: 64,
  /** Hub za hubem umí nafouknout strom; víc než 64 zařízení na jednom stroji je nesmysl. */
  usbDevices: 64,
  /** Síťová rozhraní / bloková zařízení za jedním USB kusem — reálně 1, se stropem pro jistotu. */
  usbLinks: 8,
  procRows: 200,
  procLimitDefault: 40,
  cmdLen: 256,
  pathLen: 128,
  labelLen: 64,
  errorLen: 200,
  filterLen: 100,
  hosts: 8,
  /** Klienti asociovaní na AP; rádio zvládne 128, ale tolik jich tu nikdy nebude. */
  wifiClients: 64,
  /** Řádků logu AP ve snapshotu. Ring buffer na zařízení má 1000, tolik po drátě nechceme. */
  logRows: 120,
  /** Tvrdý strop celého snapshotu po JSON.stringify. */
  snapshotBytes: 256 * 1024,
} as const;
